/**
 * Upload Notifications
 * Toast-style notifications for upload events (Google Drive style).
 *
 * Important: subscribes to the queue exactly ONCE on mount and tracks the
 * previous queue snapshot in a ref (not state) so the subscribe callback
 * doesn't carry stale closures and doesn't re-fire on every queue update.
 * Without this, every status transition was creating duplicate toasts —
 * which is what produced the "same error repeats many times" reports.
 */

import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle, AlertCircle, Upload, X } from 'lucide-react';
import { uploadQueueManager, UploadTask } from '../services/uploadQueueManager';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'info';
  title: string;
  message: string;
  timestamp: number;
  autoClose?: boolean;
}

export function UploadNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const previousQueueRef = useRef<UploadTask[]>([]);
  // Tracks notification IDs we've already raised so we never duplicate.
  const seenIdsRef = useRef<Set<string>>(new Set());
  const startBatchSeenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    previousQueueRef.current = uploadQueueManager.getQueue();

    const unsubscribe = uploadQueueManager.subscribe((currentQueue) => {
      const prev = previousQueueRef.current;
      const prevById = new Map(prev.map(t => [t.id, t]));

      // Per-task transitions: completed / failed (one notification per real transition)
      currentQueue.forEach((task) => {
        const prevTask = prevById.get(task.id);
        const prevStatus = prevTask?.status;

        if (task.status === 'completed' && prevStatus !== 'completed') {
          const nid = `complete-${task.id}`;
          if (!seenIdsRef.current.has(nid)) {
            seenIdsRef.current.add(nid);
            addNotification({
              id: nid, type: 'success',
              title: 'Upload Complete',
              message: `${task.file?.name || 'File'} uploaded successfully`,
              timestamp: Date.now(),
              autoClose: true,
            });
          }
        }

        if (task.status === 'failed' && prevStatus !== 'failed') {
          // Dedup error per task — one toast per task per failure round.
          // Subsequent retries of the same task get a fresh notification only
          // after the user manually retries (status goes pending → failed again).
          const nid = `error-${task.id}-${task.endTime || 'na'}`;
          if (!seenIdsRef.current.has(nid)) {
            seenIdsRef.current.add(nid);
            addNotification({
              id: nid, type: 'error',
              title: 'Upload Failed',
              message: task.error || `${task.file?.name || 'File'} failed to upload`,
              timestamp: Date.now(),
              autoClose: false,
            });
          }
        }

        // When the user manually retries, drop the previous "error" id from the
        // seen set so a future failure can produce a fresh toast.
        if (task.status === 'pending' && prevStatus === 'failed') {
          for (const id of Array.from(seenIdsRef.current)) {
            if (id.startsWith(`error-${task.id}-`)) seenIdsRef.current.delete(id);
          }
        }
      });

      // Batch "X file(s) added to upload queue" — emit once per add.
      const newPendingTasks = currentQueue.filter(t =>
        t.status === 'pending' &&
        !prevById.has(t.id) &&
        !startBatchSeenRef.current.has(t.id)
      );
      if (newPendingTasks.length > 0) {
        newPendingTasks.forEach(t => startBatchSeenRef.current.add(t.id));
        addNotification({
          id: `upload-start-${Date.now()}-${newPendingTasks.length}`,
          type: 'info',
          title: 'Upload Started',
          message: `${newPendingTasks.length} file${newPendingTasks.length > 1 ? 's' : ''} added to upload queue`,
          timestamp: Date.now(),
          autoClose: true,
        });
      }

      previousQueueRef.current = currentQueue;
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount-only

  const addNotification = (notification: Notification) => {
    setNotifications(prev => {
      // Final dedup at render layer — ID must be unique
      if (prev.find(p => p.id === notification.id)) return prev;
      return [notification, ...prev].slice(0, 5);
    });
    if (notification.autoClose) {
      setTimeout(() => removeNotification(notification.id), 5000);
    }
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  if (notifications.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[60] space-y-2 max-w-sm">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className={`bg-white dark:bg-gray-800 shadow-lg rounded-lg border-l-4 p-4 flex items-start gap-3 ${
            notification.type === 'success'
              ? 'border-green-500'
              : notification.type === 'error'
              ? 'border-red-500'
              : 'border-blue-500'
          }`}
          style={{ animation: 'slideIn 0.3s ease-out' }}
        >
          <div className="flex-shrink-0">
            {notification.type === 'success' && <CheckCircle className="w-6 h-6 text-green-500" />}
            {notification.type === 'error' && <AlertCircle className="w-6 h-6 text-red-500" />}
            {notification.type === 'info' && <Upload className="w-6 h-6 text-blue-500" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-gray-900 dark:text-white">{notification.title}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 break-words">{notification.message}</p>
          </div>
          <button
            onClick={() => removeNotification(notification.id)}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(400px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
